/**
 * Gebaeudeumrisse (PV-D18).
 *
 * Ein vorhandener Umriss ist als Ausgangspolygon fast immer besser als alles,
 * was sich aus dem Bild rechnen laesst: Er hat saubere Ecken, rechte Winkel und
 * ist bereits vereinfacht.
 *
 * Vektorumrisse sind kleine Datenmengen — ein Ortsteil sind ein paar Dutzend
 * Kilobyte. Deshalb werden sie zusammen mit den Kartenkacheln vorgeladen und
 * liegen danach lokal. Der Vorschlag funktioniert damit auch ohne Empfang.
 */

import { punktInnerhalb, bezugspunkt, nachGrundriss, type Geo } from '@pv/geometrie';

import { anmeldung } from './abgleich.ts';
import { db } from './db.ts';

/** bev = amtlicher Kataster, osm = OpenStreetMap. Steht in der Oberflaeche dran. */
export type Gebaeudequelle = 'bev' | 'osm';

export interface Gebaeude {
  id: string;
  projekt_id: string;
  umriss: Geo[];
  quelle: Gebaeudequelle;
  geladen_am: string;
}

/**
 * Die Umrisse kommen ueber den eigenen Server, nicht direkt von Overpass.
 *
 * Overpass ist frei, stark genutzt und faellt regelmaessig aus — am
 * 14.09.2026 sechsmal 504 hintereinander, kurz darauf 145 Gebaeude. Wer genau
 * in diesem Moment vorbereitet, faehrt ohne Umrisse los und merkt es erst beim
 * Kunden. Der Server wiederholt, hebt das Ergebnis auf und liefert im
 * Zweifel einen alten Stand — Haeuser aendern sich selten.
 */
export class KeineUmrisse extends Error {
  constructor(meldung: string) {
    super(meldung);
    this.name = 'KeineUmrisse';
  }
}

export async function ladeGebaeude(
  projektId: string,
  lat: number,
  lng: number,
  umkreisM: number,
): Promise<number> {
  const an = await anmeldung();
  const antwort = await fetch(
    `/api/gebaeude?lat=${lat}&lng=${lng}&umkreis=${Math.round(umkreisM)}`,
    { headers: an ? { Authorization: `Bearer ${an.token}` } : {} },
  );
  if (!antwort.ok) {
    const daten = (await antwort.json().catch(() => ({}))) as { fehler?: string };
    throw new KeineUmrisse(daten.fehler ?? `Gebäudeumrisse: ${antwort.status}`);
  }

  const daten = (await antwort.json()) as {
    umrisse: Array<{ id: string; umriss: Geo[] }>;
    herkunft: string;
    quelle?: Gebaeudequelle;
    meldung?: string;
  };
  // Eine leere Antwort ersetzt keinen vorhandenen Bestand: Fällt der Kataster
  // aus und der Rückfall liefert nichts, blieben sonst vor Ort gar keine
  // Umrisse — gerade dort, wo kein Empfang ist, um es noch einmal zu versuchen.
  if (daten.umrisse.length === 0) {
    const vorhanden = await db.gebaeudeumrisse.where('projekt_id').equals(projektId).count();
    if (vorhanden > 0) return vorhanden;
  }
  const jetzt = new Date().toISOString();
  // Ersetzen, nicht ergaenzen. Die Kennungen tragen die Quelle im Namen
  // ("bev-…", "osm-…"), also wuerde ein blosses Hinzufuegen beide Bestaende
  // nebeneinander stehen lassen — auf der Karte zwei Umrisse je Haus, leicht
  // gegeneinander versetzt. Ein Vorbereiten liefert immer den ganzen Umkreis,
  // deshalb ist der vorige Stand danach ohnehin ueberholt.
  await db.transaction('rw', db.gebaeudeumrisse, async () => {
    await db.gebaeudeumrisse.where('projekt_id').equals(projektId).delete();
    await db.gebaeudeumrisse.bulkPut(
      daten.umrisse.map((g) => ({
        id: g.id,
        projekt_id: projektId,
        umriss: g.umriss,
        quelle: daten.quelle ?? 'osm',
        geladen_am: jetzt,
      })),
    );
  });
  return daten.umrisse.length;
}

/** Welches Gebaeude liegt unter dem Klick? */
export async function gebaeudeAn(projektId: string, klick: Geo): Promise<Gebaeude | undefined> {
  const alle = await db.gebaeudeumrisse.where('projekt_id').equals(projektId).toArray();
  for (const gebaeude of alle) {
    const bezug = bezugspunkt(gebaeude.umriss);
    const ring = gebaeude.umriss.map((p) => nachGrundriss(p, bezug));
    if (punktInnerhalb(nachGrundriss(klick, bezug), ring)) return gebaeude;
  }
  return undefined;
}

export async function anzahlGebaeude(projektId: string): Promise<number> {
  return db.gebaeudeumrisse.where('projekt_id').equals(projektId).count();
}
